import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  API,
  AUTH,
  BASE,
  ROUTES,
  LoginResponse,
  V1,
  addTokenCookies,
  URL_LOGIN,
  addCredentialsCookies,
  removeCredentialsCookies,
  getCredentialsCookies,
} from '@jobcost-monorepo/utils';
import { schema } from './schema';

export type FormFieldsLogin = {
  email: string;
  password: string;
  tenantId?: string;
  rememberMe: boolean;
};

type Return = {
  methods: UseFormReturn<FormFieldsLogin>;
  loading: boolean;
  error: string;
  resetPasswordLink: string;
  handleLogin: (data: FormFieldsLogin) => Promise<void>;
};

const useLogin = (): Return => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const email = getCredentialsCookies()?.email;

  const resetPasswordLink = ROUTES.FORGOT_PASSWORD;

  const methods = useForm<FormFieldsLogin>({
    defaultValues: {
      email: email || '',
      password: '',
      rememberMe: email !== undefined,
    },
    resolver: zodResolver(schema),
  });

  const handleLogin = async ({
    email,
    password,
    rememberMe = false,
  }: FormFieldsLogin) => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BASE}${API}${V1}${AUTH}${URL_LOGIN}`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200 || response.status === 201) {
        setLoading(false);
        const data: LoginResponse = await response.json();
        if (data?.user?.stsTokenManager.accessToken) {
          addTokenCookies({
            accessToken: data?.user?.stsTokenManager.accessToken,
            refreshToken: data?.user?.stsTokenManager.refreshToken,
          });

          rememberMe
            ? addCredentialsCookies({ email })
            : removeCredentialsCookies();

          return navigate(`/${ROUTES.DASHBOARD}`);
        }
      }
      setLoading(false);
      setError('wrongLogin');
      return;
    } catch (error) {
      setLoading(false);
    }
  };

  return {
    methods,
    loading,
    error,
    resetPasswordLink,
    handleLogin,
  };
};

export default useLogin;
